import Singleton from './_ugly/Singleton';
import clamp from './helpers/clamp';
import distance from './helpers/distance';
import isMobileDevice from './helpers/isMobileDevice';
import precision from './helpers/precision';

const noop = () => {};

function debounce(func, timeout = 300) {
  let timer;

  return (...args) => {
    clearTimeout(timer);

    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
}

const options = {
  MAX_CANVAS_SIZE: 4096,
  NOMINAL_CROP: 0.8,
  MOVE_DISTANCE: 10,
  WHEEL_STEP: 200,
  PADDING: 40,
  MIN_SCALE: 0.2,
  MAX_SCALE: 2.0,
  RESIZE_DEBOUNCE_TIME: 500,
};

const IS_MOBILE = isMobileDevice();

class Viewport {
  zoom = 0;

  oldZoom = 0;

  referenceZoom = 0;

  translation = [0, 0];

  transformationStart = [0, 0];

  rectangleCenter = [0, 0];

  wheelPosition = 0;

  baseDistance = 0;

  isPainting = false;

  isTransforming = false;

  isTouched = false;

  isTranslating = false;

  isBlocked = false;

  constructor(canvas, w, h, _, onClose) {
    this.canvas = canvas;
    this.onClose = onClose;

    this.textureWidth = w;
    this.textureHeight = h;

    this.setHandlers(noop, noop, noop, noop);

    this.bindCallbacks();

    this.calculateCanvasSizes();

    this.calculateViewportScale();
    this.calculateCenters();

    this.editCanvas();

    this.buildUI();

    this.addListeners();

    this.changeViewport();

    this.fitToWindow();

    this.toolBox = document.getElementById('tool-box');
    this.root = document.getElementById('root');
  }

  enableUi() {
    this.toolBox.style.pointerEvents = 'unset';
    this.root.style.pointerEvents = 'unset';
  }

  disableUi() {
    this.toolBox.style.pointerEvents = 'none';
    this.root.style.pointerEvents = 'none';
  }

  setHandlers(startMoving, moving, stopMoving, changeViewport) {
    this.handleStartMoving = startMoving;
    this.handleStopMoving = stopMoving;
    this.handleMoving = moving;

    if (changeViewport) {
      this.handleChangeViewport = changeViewport;
    }
  }

  calculateCanvasSizes() {
    const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    const height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;

    this.canvasWidth = Math.min(width, options.MAX_CANVAS_SIZE);
    this.canvasHeight = Math.min(height, options.MAX_CANVAS_SIZE);
  }

  calculateViewportScale() {
    const nominalWidth = this.canvasWidth * options.NOMINAL_CROP;
    const nominalHeight = this.canvasHeight * options.NOMINAL_CROP;

    const scaleWidth = nominalWidth / this.textureWidth;
    const scaleHeight = nominalHeight / this.textureHeight;

    this.viewportScale = Math.min(scaleWidth, scaleHeight);

    this.maxZoom = 2 * Math.log2(options.MAX_SCALE / this.viewportScale);
    this.minZoom = 2 * Math.log2(Math.min(options.MIN_SCALE, this.viewportScale) / this.viewportScale);

    this.translation = [0, 0];
  }

  calculateCenters() {
    this.rectangleCenter = [this.canvasWidth / 2, this.canvasHeight / 2];
    this.transformationStart = [...this.rectangleCenter];
  }

  editCanvas() {
    this.canvas.width = this.canvasWidth;
    this.canvas.height = this.canvasHeight;
  }

  getCanvas() {
    return this.canvas;
  }

  bindCallbacks() {
    this.onResize = this.onResize.bind(this);

    this.changeViewport = this.changeViewport.bind(this);

    this.onWheel = this.onWheel.bind(this);

    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);

    this.scaleOnPointerDown = this.scaleOnPointerDown.bind(this);
    this.scaleOnPointerUp = this.scaleOnPointerUp.bind(this);
    this.scaleOnPointerMove = this.scaleOnPointerMove.bind(this);

    this.scaleOnTouchStart = this.scaleOnTouchStart.bind(this);
    this.scaleOnTouchMove = this.scaleOnTouchMove.bind(this);
    this.scaleOnTouchEnd = this.scaleOnTouchEnd.bind(this);
  }

  buildUI() {
    this.transformArea = document.createElement('div');
    this.transformArea.className = 'transform-area';
    document.body.appendChild(this.transformArea);
  }

  addListeners() {
    const resize = debounce(this.onResize, options.RESIZE_DEBOUNCE_TIME);

    window.addEventListener('resize', resize, false);

    this.canvas.addEventListener('wheel', this.onWheel, false);
    this.transformArea.addEventListener('wheel', this.onWheel, false);

    if (IS_MOBILE) {
      this.canvas.addEventListener('touchstart', this.onTouchStart, false);
      this.canvas.addEventListener('touchend', this.onTouchEnd, false);

      this.transformArea.addEventListener('touchstart', this.scaleOnTouchStart, false);
      this.transformArea.addEventListener('touchend', this.scaleOnTouchEnd, false);
    } else {
      this.canvas.addEventListener('pointerdown', this.onPointerDown, false);
      this.canvas.addEventListener('pointerup', this.onPointerUp, false);

      this.transformArea.addEventListener('pointerdown', this.scaleOnPointerDown, false);
      this.transformArea.addEventListener('pointerup', this.scaleOnPointerUp, false);

      this.transformArea.addEventListener(
        'dblclick',
        () => {
          this.onClose();
        },
        false
      );
    }
  }

  changeViewport(eventFromWheel = false) {
    this.calculateViewport();

    if (eventFromWheel) {
      this.normalizePosition();
    }

    this.handleChangeViewport();
  }

  onWheel(e) {
    if (this.isPainting || this.isTranslating) {
      return;
    }

    this.wheelPosition -= e.deltaY;
    this.transformationStart = [e.clientX, this.canvasHeight - e.clientY];

    this.changeZoomByWheel();
  }

  changeZoomByWheel() {
    const zoom = this.wheelPosition / options.WHEEL_STEP;

    if (zoom >= this.maxZoom && this.maxWheelPosition === undefined) {
      this.maxWheelPosition = this.wheelPosition;
    }

    if (zoom <= this.minZoom && this.minWheelPosition === undefined) {
      this.minWheelPosition = this.wheelPosition;
    }

    if (this.minWheelPosition !== undefined) {
      this.wheelPosition = Math.max(this.wheelPosition, this.minWheelPosition);
    }

    if (this.maxWheelPosition !== undefined) {
      this.wheelPosition = Math.min(this.wheelPosition, this.maxWheelPosition);
    }

    this.zoom = clamp(this.minZoom, this.maxZoom, zoom);

    this.changeViewport(true);
  }

  onResize() {
    this.calculateCanvasSizes();

    this.canvas.width = this.canvasWidth;
    this.canvas.height = this.canvasHeight;

    this.calculateViewportScale();

    this.setDefaultZoom();
  }

  reset(zoom = 0) {
    this.zoom = zoom;

    this.calculateCenters();

    this.translation = [0, 0];
    this.maxWheelPosition = undefined;
    this.minWheelPosition = undefined;
    this.wheelPosition = precision(zoom, 2) * options.WHEEL_STEP;
  }

  setDefaultZoom() {
    this.reset();
    this.changeViewport();
  }

  fitToNaturalScale() {
    this.reset(2 * Math.log2(1.0 / this.viewportScale));
    this.changeViewport();
  }

  fitToWindow() {
    this.reset(0);
    this.changeViewport();
  }

  transforming(event1, event2) {
    if (!this.isTransforming) {
      this.baseDistance = distance(event1, event2);

      this.transformationStart = [(event1.clientX + event2.clientX) / 2, this.canvasHeight - (event1.clientY + event2.clientY) / 2];

      this.translation = [0, 0];

      this.referenceZoom = this.zoom;
    } else {
      const newDistance = distance(event1, event2);

      if (this.baseDistance > 0) {
        const step = (this.baseDistance - newDistance) / 100;

        const newZoom = this.referenceZoom - step;
        this.zoom = Math.max(Math.min(newZoom, this.maxZoom), this.minZoom);

        const currentCenter = [(event1.clientX + event2.clientX) / 2, this.canvasHeight - (event1.clientY + event2.clientY) / 2];

        this.translation = [currentCenter[0] - this.transformationStart[0], currentCenter[1] - this.transformationStart[1]];

        this.changeViewport();
      }
    }
  }

  calculateViewport() {
    const oldScale = this.viewportScale * Math.pow(2, this.oldZoom / 2);
    const scale = this.viewportScale * Math.pow(2, this.zoom / 2);

    const deltaScale = scale / oldScale;

    const w = this.textureWidth * scale;
    const h = this.textureHeight * scale;

    this.rectangleCenter[0] = (this.rectangleCenter[0] - this.transformationStart[0]) * deltaScale + this.transformationStart[0];
    this.rectangleCenter[1] = (this.rectangleCenter[1] - this.transformationStart[1]) * deltaScale + this.transformationStart[1];

    const t = this.rectangleCenter[1] - h / 2;
    const l = this.rectangleCenter[0] - w / 2;

    this.viewport = {
      width: w,
      height: h,
      top: t + this.translation[1],
      left: l + this.translation[0],
    };

    this.oldZoom = this.zoom;

    this.boundViewport();

    this.calculateXYScales();
  }

  calculateXYScales() {
    this.positionScaleX = this.textureWidth / this.viewport.width;
    this.positionScaleY = this.textureHeight / this.viewport.height;
  }

  boundViewport() {
    this.viewport.left = Math.max(Math.min(this.viewport.left, this.canvasWidth - options.PADDING), options.PADDING - this.viewport.width);

    this.viewport.top = Math.max(Math.min(this.viewport.top, this.canvasHeight - options.PADDING), options.PADDING - this.viewport.height);
  }

  normalizePosition() {
    this.translation = [0, 0];
    this.rectangleCenter[0] = this.viewport.left + this.viewport.width / 2;
    this.rectangleCenter[1] = this.viewport.top + this.viewport.height / 2;
  }

  painting(event, getPressureFromEvent = false) {
    const coords = this.getCoordinatesInTextureViewport(event.clientX, event.clientY);

    if (event.touchType === 'direct') {
      getPressureFromEvent = false;
    }

    const force = getPressureFromEvent ? event.force : 1.0;

    Singleton.get('force-indicator').alwaysUpdate(force);

    if (!this.isPainting) {
      this.handleStartMoving(coords, force);
    } else {
      this.handleMoving(coords, force);
    }
  }

  stopPainting(event, getPressureFromEvent = false) {
    const coords = this.getCoordinatesInTextureViewport(event.clientX, event.clientY);

    if (event.touchType === 'direct') {
      getPressureFromEvent = false;
    }

    this.handleStopMoving(coords, getPressureFromEvent ? event.force : 1.0);

    Singleton.get('force-indicator').alwaysUpdate(0);
  }

  getCoordinatesInTextureViewport(x, y) {
    const localX = (x - this.viewport.left) * this.positionScaleX;
    const localY = (this.canvasHeight - y - this.viewport.top) * this.positionScaleY;

    return [localX, localY];
  }

  update(state) {
    if (state.controlling.showTransformArea) {
      this.transformArea.style.display = 'block';
    } else {
      this.transformArea.style.display = 'none';
    }
  }

  onPointerDown(e) {
    this.disableUi();

    e.preventDefault();

    this.painting(e, false);

    this.isPainting = true;

    this.canvas.addEventListener('pointermove', this.onPointerMove, false);
  }

  onPointerUp(e) {
    this.enableUi();

    e.preventDefault();

    if (this.isPainting) {
      this.stopPainting(e, false);
    }

    this.isPainting = false;

    this.canvas.removeEventListener('pointermove', this.onPointerMove, false);
  }

  onPointerMove(e) {
    e.preventDefault();

    this.painting(e, false);
  }

  onTouchStart(e) {
    this.disableUi();

    e.preventDefault();

    this.painting(e.targetTouches[0], true);

    this.isPainting = true;

    this.canvas.addEventListener('touchmove', this.onTouchMove, false);
  }

  onTouchMove(e) {
    e.preventDefault();

    this.painting(e.targetTouches[0], true);
  }

  onTouchEnd(e) {
    this.enableUi();

    e.preventDefault();

    if (this.isPainting) {
      this.stopPainting(e.changedTouches[0], true);
    }

    this.isPainting = false;

    this.canvas.removeEventListener('touchmove', this.onTouchMove, false);
  }

  scaleOnPointerDown(e) {
    if (e.pointerType !== 'mouse') {
      return;
    }

    e.preventDefault();

    this.isTranslating = true;

    this.transformationStart = [e.clientX, this.canvasHeight - e.clientY];
    this.translation = [0, 0];

    this.transformArea.addEventListener('pointermove', this.scaleOnPointerMove, false);
  }

  scaleOnPointerUp(e) {
    e.preventDefault();

    if (this.isTranslating) {
      this.normalizePosition();
    }

    this.isTranslating = false;

    this.transformArea.removeEventListener('pointermove', this.scaleOnPointerMove, false);
  }

  scaleOnPointerMove(e) {
    e.preventDefault();

    this.translation = [e.clientX - this.transformationStart[0], this.canvasHeight - e.clientY - this.transformationStart[1]];

    this.changeViewport();
  }

  ////////////////////////////////////////////////

  dbTouch = undefined;

  dbTouchTime = undefined;

  scaleOnTouchStart(e) {
    e.preventDefault();

    this.transformArea.addEventListener('touchmove', this.scaleOnTouchMove, false);
  }

  scaleOnTouchMove(e) {
    e.preventDefault();

    if (this.isBlocked) {
      return;
    }

    const howManyFingers = e.targetTouches.length;

    if (!this.isTouched) {
      this.touches = e.targetTouches;

      this.isTouched = true;
    }

    if (howManyFingers === 2) {
      this.transforming(e.targetTouches[0], e.targetTouches[1]);

      this.isTransforming = true;
    }
  }

  scaleOnTouchEnd(e) {
    e.preventDefault();

    let close = false;

    if (e.targetTouches.length === 0) {
      if (this.isTransforming) {
        this.normalizePosition();
      } else {
        const x = e.changedTouches[0].clientX;
        const y = e.changedTouches[0].clientY;

        if (this.dbTouch) {
          const delta = Date.now() - this.dbTouchTime;

          if (delta < 200) {
            const dx = Math.abs(x - this.dbTouch[0]);
            const dy = Math.abs(y - this.dbTouch[1]);

            if (dx < 40 && dy < 40) {
              this.dbTouch = undefined;

              close = true;
            }
          }
        }

        this.dbTouch = [x, y];
        this.dbTouchTime = Date.now();
      }

      this.isBlocked = false;
      this.isTouched = false;

      this.isTransforming = false;

      this.transformArea.removeEventListener('touchmove', this.scaleOnTouchMove, false);
    } else {
      this.isBlocked = true;
    }

    if (close) {
      this.onClose();
    }
  }
}

export default Viewport;
